import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { creditsAPI } from 'services/transactionService';
import ActionDetailPanel from 'components/FlipPanel/ActionDetailPanel';

// Custom styles - moved outside component to prevent recreation
const customStyles = {
  gradientCard: { 
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none'
  },
  iconContainer: {
    width: '40px', 
    height: '40px', 
    backgroundColor: '#e9ecef',
    borderRadius: '50%'
  }
};

const BillingActivity = () => {
  const [activities, setActivities] = useState([]);
  const [actions, setActions] = useState([]);
  const [totalActionsCount, setTotalActionsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('actions');
  const [balance, setBalance] = useState(null);
  const [filterActionType, setFilterActionType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Use ref to track if count needs to be refreshed
  const countNeedsRefresh = useRef(true);

  // Fetch balance, activities and initial data
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceData, activitiesData] = await Promise.all([
        creditsAPI.getBalance(),
        creditsAPI.getActivities()
      ]);
      setBalance(balanceData.data || null);
      setActivities(activitiesData.data.activities || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      alert('Failed to load activity history. Please try again.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch actions count only when needed
  const fetchActionsCount = useCallback(async () => {
    try {
      const countData = await creditsAPI.getActionsCount();
      setTotalActionsCount(countData.data.total || 0);
      countNeedsRefresh.current = false;
    } catch (error) {
      console.error('Error fetching actions count:', error);
      setTotalActionsCount(0);
    }
  }, []);

  // Fetch actions data with offset
  const fetchActions = useCallback(async (page = currentPage, limit = itemsPerPage) => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;
      const actionsData = await creditsAPI.getActions(limit, offset);
      setActions(actionsData.data.actions || []);
    } catch (error) {
      console.error('Error fetching actions:', error);
      alert('Failed to load actions. Please try again.');
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  // Fetch initial data on mount
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch actions count when switching to actions tab (only once)
  useEffect(() => {
    if (activeTab === 'actions') {
      if (countNeedsRefresh.current) {
        fetchActionsCount();
      }
    }
  }, [activeTab, fetchActionsCount]);
  
  // Also fetch count on initial mount if actions tab is active
  useEffect(() => {
    if (activeTab === 'actions' && totalActionsCount === 0) {
      fetchActionsCount();
    }
  }, [activeTab, totalActionsCount, fetchActionsCount]);

  // Fetch actions when on actions tab and page/itemsPerPage changes
  useEffect(() => {
    if (activeTab === 'actions') {
      fetchActions(currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage, activeTab, fetchActions]);

  // Reset to first page only when filters actually change (not on initial render or page navigation)
  const prevFilters = useRef({ category: '', actionType: '' });
  useEffect(() => {
    if (activeTab === 'actions') {
      const filterChanged = 
        prevFilters.current.category !== filterCategory || 
        prevFilters.current.actionType !== filterActionType;
      
      if (filterChanged && (filterCategory || filterActionType) && currentPage !== 1) {
        setCurrentPage(1);
      }
      
      prevFilters.current = { category: filterCategory, actionType: filterActionType };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterActionType, activeTab]);

  // Refresh all data
  const fetchData = useCallback(async () => {
    await fetchInitialData();
    if (activeTab === 'actions') {
      countNeedsRefresh.current = true;
      await fetchActionsCount();
      await fetchActions();
    }
  }, [activeTab, fetchInitialData, fetchActionsCount, fetchActions]);

  const formatDate = (dateValue) => {
    // Handle Unix timestamp (seconds since 1970) or regular date strings
    let date;
    
    try {
      if (typeof dateValue === 'number' || (typeof dateValue === 'string' && /^\d+$/.test(dateValue))) {
        // Unix timestamp - convert to milliseconds and create Date
        const unixTimestamp = parseInt(dateValue);
        
        // Validate Unix timestamp (should be reasonable range)
        if (unixTimestamp > 0 && unixTimestamp < 4102444800) { // Between 1970 and 2100
          date = new Date(unixTimestamp * 1000);
        } else {
          // Fallback to current time if timestamp seems invalid
          date = new Date();
        }
      } else {
        // Regular date string
        date = new Date(dateValue);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        date = new Date(); // Fallback to current time
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      // Fallback to current time
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getActionIcon = (actionType) => {
    const iconMap = {
      // SEO actions
      'seo_title_single_keyword': 'search-green.svg',
      'seo_title_url': 'search-green.svg', 
      'seo_meta_description_single_keyword': 'search-green.svg',
      'seo_meta_description_url': 'search-green.svg',
      'seo_article_headline_keyword': 'search-green.svg',
      'seo_article_headline_url': 'search-green.svg',
      'seo_image_optimisation_single_keyword': 'search-green.svg',
      // 'seo_article_outline': 'search-green.svg',
      // 'seo_article_content_tags': 'search-green.svg',
      // 'seo_article_content_tags_url': 'search-green.svg',
      'keyword_ideas_single_keyword': 'search-green.svg',
      'keyword_ideas_url': 'search-green.svg',
      'single_keyword_ranking': 'ranking.svg',
      'domain_ranking': 'ranking.svg',
      'page_ranking': 'ranking.svg',
            
      // Writer actions
      'article_headline_single_keyword': 'edit.svg',
      'article_headline_url': 'edit.svg',
      'page_level_content_outliner': 'component.svg',
      'keyword_level_content_outliner': 'edit-two.svg',
      'page_level_content_draft': 'edit.svg',
      'keyword_level_content_draft': 'edit-two.svg',
      'ai_overview_keyword': 'edit.svg',
      'ai_overview_url': 'edit-two.svg',
      'ai_overview_content': 'edit-two.svg',
      'copy_and_paste_content_by_keyword': 'edit-two.svg',
      
      // Social actions
      'meta_and_insta_tags': 'thumbs-up.svg',
      'meta_and_insta_content_creation': 'application-two.svg',
      
      'default': 'more-app.svg'
    };

    const icon = iconMap[actionType] || iconMap.default;
    return `/icon/${icon}`;
  };

  const getActionColor = (actionType) => {
    const colorMap = {
      // SEO actions
      'seo_title_single_keyword': 'bg-blue-100 text-blue-600',
      'seo_title_url': 'bg-cyan-100 text-cyan-600',
      'seo_meta_description_single_keyword': 'bg-green-100 text-green-600',
      // 'seo_meta_description_multiple_keywords': 'bg-green-100 text-green-600',
      'seo_meta_description_url': 'bg-teal-100 text-teal-600',
      'seo_article_headline_keyword': 'bg-purple-100 text-purple-600',
      'seo_article_headline_url': 'bg-indigo-100 text-indigo-600',
      'seo_image_optimisation_single_keyword': 'bg-yellow-100 text-yellow-600',
      // 'seo_article_outline': 'bg-indigo-100 text-indigo-600',
      // 'seo_article_content_tags': 'bg-pink-100 text-pink-600',
      // 'seo_article_content_tags_url': 'bg-pink-100 text-pink-600',
      'keyword_ideas_single_keyword': 'bg-emerald-100 text-emerald-600',
      // 'keyword_ideas_multiple_keywords': 'bg-emerald-100 text-emerald-600',
      'keyword_ideas_url': 'bg-lime-100 text-lime-600',
      'single_keyword_ranking': 'bg-red-100 text-red-600',
      'domain_ranking': 'bg-orange-100 text-orange-600',
      'page_ranking': 'bg-amber-100 text-amber-600',
            
      // Writer actions
      'article_headline_single_keyword': 'bg-violet-100 text-violet-600',
      'article_headline_url': 'bg-indigo-100 text-indigo-600',
      'page_level_content_outliner': 'bg-purple-100 text-purple-600',
      'keyword_level_content_outliner': 'bg-fuchsia-100 text-fuchsia-600',
      'page_level_content_draft': 'bg-rose-100 text-rose-600',
      'keyword_level_content_draft': 'bg-pink-100 text-pink-600',
      'ai_overview_keyword': 'bg-violet-100 text-violet-600',
      'ai_overview_url': 'bg-purple-100 text-purple-600',
      'ai_overview_content': 'bg-fuchsia-100 text-fuchsia-600',
      'copy_and_paste_content_by_keyword': 'bg-rose-100 text-rose-600',
      
      // Social actions
      'meta_and_insta_tags': 'bg-sky-100 text-sky-600',
      'meta_and_insta_content_creation': 'bg-slate-100 text-slate-600',
      
      'default': 'bg-gray-100 text-gray-600'
    };

    return colorMap[actionType] || colorMap.default;
  };


  // Memoized: Get unique action types from current actions
  const uniqueActionTypes = useMemo(() => {
    const actionTypes = [...new Set(actions.map(action => action.action_type))];
    return actionTypes.sort();
  }, [actions]);

  // Memoized: Get unique categories from activities
  const uniqueCategories = useMemo(() => {
    const categories = [...new Set(activities.map(activity => activity.category))];
    return categories.sort();
  }, [activities]);

  // Memoized: Get unique action types for a specific category
  const uniqueActionTypesForCategory = useMemo(() => {
    if (!filterCategory) return [];
    const categoryActionTypes = activities
      .filter(activity => activity.category === filterCategory)
      .map(activity => activity.action_type);
    return [...new Set(categoryActionTypes)].sort();
  }, [activities, filterCategory]);

  // Memoized: Filter actions based on selected filters (client-side filtering on current page)
  const filteredActionsForDisplay = useMemo(() => {
    let filtered = actions;
    
    if (filterCategory && uniqueActionTypesForCategory.length > 0) {
      filtered = filtered.filter(action => uniqueActionTypesForCategory.includes(action.action_type));
    }
    
    if (filterActionType) {
      filtered = filtered.filter(action => action.action_type === filterActionType);
    }
    
    return filtered;
  }, [actions, filterCategory, filterActionType, uniqueActionTypesForCategory]);

  // Memoized: Filter activities based on selected filters
  const filteredActivities = useMemo(() => {
    if (!filterCategory) return activities;
    return activities.filter(activity => activity.category === filterCategory);
  }, [activities, filterCategory]);

  // Memoized: Calculate total pages based on total count from server
  const totalPages = useMemo(() => {
    return Math.ceil(totalActionsCount / itemsPerPage) || 1;
  }, [totalActionsCount, itemsPerPage]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    const pageNum = parseInt(page, 10);
    if (pageNum >= 1 && pageNum <= totalPages && pageNum !== currentPage && !loading) {
      setCurrentPage(pageNum);
      // Scroll to top smoothly after state update
      setTimeout(() => {
        const element = document.querySelector('.card-header');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    }
  }, [currentPage, totalPages, loading]);

  const clearFilters = () => {
    setFilterCategory('');
    setFilterActionType('');
  };

  // Memoized: Check if action type should have "View More" button
  const viewMoreActionsSet = useMemo(() => new Set([
    'page_level_content_outliner',
    'keyword_level_content_outliner', 
    'page_level_content_draft',
    'keyword_level_content_draft',
    'ai_overview_keyword',
    'ai_overview_url',
    'ai_overview_content',
    'copy_and_paste_content_by_keyword',
    'article_headline_single_keyword',
    'article_headline_url'
  ]), []);

  const shouldShowViewMore = useCallback((actionType) => {
    return viewMoreActionsSet.has(actionType);
  }, [viewMoreActionsSet]);

  // Handle opening detail panel
  const handleViewMore = (action) => {
    setSelectedAction(action);
    setShowDetailPanel(true);
  };

  // Handle closing detail panel
  const handleClosePanel = () => {
    setShowDetailPanel(false);
    setSelectedAction(null);
  };


  if (loading) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading activity history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="d-flex justify-content-center">
        <div className="container-fluid px-3 px-md-4 px-lg-5" style={{ maxWidth: '1400px', width: '100%' }}>
            {/* Header */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 w-100">
              <div className="text-center text-md-start mb-3 mb-md-0">
                <h1 className="h2 fw-bold text-dark mb-2">
                  Past Activities + Available Activities
                </h1>
                <p className="text-muted mb-0">
                  View your credit usage and activity history
                </p>
              </div>
              <button
                onClick={fetchData}
                disabled={loading}
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="spinner-border spinner-border-sm text-white" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt"></i>
                    Refresh
                  </>
                )}
              </button>
            </div>

            {/* Account Balance Card */}
            {balance && (
              <div className="card text-white mb-4" style={customStyles.gradientCard}>
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <h3 className="h4 fw-semibold mb-2">Account Balance</h3>
                      <div className="display-6 fw-bold">{balance.credit_balance || 0} Credits</div>
                      <p className="text-light mb-0">
                        Available for use across all services
                      </p>
                    </div>
                    <div className="col-md-6 text-md-end">
                      <div className="h3 fw-semibold">{balance.username}</div>
                      <p className="text-light mb-0">User ID: {balance.user_id}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <div className="row g-3 justify-content-center">
                  <div className="col-12 col-md-3">
                    <label className="form-label fw-medium text-dark">
                      Filter by Category
                    </label>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="form-select"
                    >
                      <option value="">All Categories</option>
                      {uniqueCategories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-12 col-md-3">
                    <label className="form-label fw-medium text-dark">
                      Filter by Action Type
                    </label>
                    <select
                      value={filterActionType}
                      onChange={(e) => setFilterActionType(e.target.value)}
                      className="form-select"
                    >
                      <option value="">All Action Types</option>
                      {filterCategory && uniqueActionTypesForCategory.map(actionType => (
                        <option key={actionType} value={actionType}>
                          {actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-12 col-md-3 d-flex align-items-end justify-content-center">
                    <button
                      onClick={clearFilters}
                      className="btn btn-secondary"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
                
                {/* Active Filters Display */}
                {(filterActionType || filterCategory) && (
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    {filterCategory && (
                      <span className="badge bg-success d-inline-flex align-items-center">
                        Category: {filterCategory}
                        <button
                          onClick={() => setFilterCategory('')}
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                        ></button>
                      </span>
                    )}
                    {filterActionType && (
                      <span className="badge bg-primary d-inline-flex align-items-center">
                        Action: {filterActionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        <button
                          onClick={() => setFilterActionType('')}
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                        ></button>
                      </span>
                    )}
                  </div>
                )}
                
                {/* Filter Help Text */}
                <div className="mt-3">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    <strong>Tip:</strong> Select a category to see all activities and actions in that category. 
                    Use the Action Type filter to further refine your results.
                  </small>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4" id="billingTabs" role="tablist">
              <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'actions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('actions')}
                    type="button"
                    role="tab"
                  >
                    Recent Actions <span className="badge bg-secondary ms-1">{totalActionsCount}</span>
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'activities' ? 'active' : ''}`}
                    onClick={() => setActiveTab('activities')}
                    type="button"
                    role="tab"
                  >
                    Available Activities <span className="badge bg-secondary ms-1">{activities.length}</span>
                  </button>
                </li>
              </ul>

            {/* Content */}
            {activeTab === 'actions' && (
              <div className="card">
                {filteredActionsForDisplay.length === 0 ? (
                  <div className="card-body text-center py-5">
                    <i className="fas fa-check-circle text-muted" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3 text-muted">
                      {actions.length === 0 ? 'No actions' : 'No actions match your filters'}
                    </h5>
                    <p className="text-muted">
                      {actions.length === 0 
                        ? "You haven't performed any actions yet."
                        : "Try adjusting your filters to see more results."
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="card-header bg-light">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <small className="text-muted">
                            Showing {filteredActionsForDisplay.length > 0 ? ((currentPage - 1) * itemsPerPage + 1) : 0} - {Math.min(currentPage * itemsPerPage, totalActionsCount)} of {totalActionsCount} actions
                            {(filterActionType || filterCategory) && ` (filtered on current page)`}
                          </small>
                        </div>
                        <div className="col-md-6 text-md-end mt-2 mt-md-0">
                          <div className="d-flex align-items-center justify-content-md-end gap-3">
                            <label className="mb-0 small text-muted d-flex align-items-center gap-2">
                              Items per page:
                              <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                  setItemsPerPage(Number(e.target.value));
                                  setCurrentPage(1);
                                }}
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                              >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                              </select>
                            </label>
                            <small className="text-muted">
                              Page credits: {filteredActionsForDisplay.reduce((sum, a) => sum + (a.credits_used || 0), 0)}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="list-group list-group-flush">
                      {filteredActionsForDisplay.map((action, index) => (
                        <div key={index} className="list-group-item">
                          <div className="row align-items-center">
                            <div className="col-auto">
                              <div className="rounded-circle d-flex align-items-center justify-content-center" 
                                   style={customStyles.iconContainer}>
                                <img
                                  src={getActionIcon(action.action_type)}
                                  alt={action.action_type}
                                  width={20}
                                  height={20}
                                />
                              </div>
                            </div>
                            <div className="col">
                              <h6 className="mb-1 fw-semibold">
                                {action.action_type ? 
                                  action.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).replace(/\bAi\b/g, 'AI') : 
                                  'Action'
                                }
                              </h6>
                              <small className="text-muted d-block">
                                {formatDate(action.created_at)}
                              </small>
                              {action.description && (
                                <small className="text-muted d-block mt-1">
                                  {action.description}
                                </small>
                              )}
                            </div>
                            <div className="col-auto text-end">
                              <div className="fw-bold text-primary mb-1">
                                {action.credits_used || 0} credits
                              </div>
                              <small className="text-muted d-block mb-2">
                                {action.status || 'Completed'}
                              </small>
                              {shouldShowViewMore(action.action_type) && (
                                <button
                                  onClick={() => handleViewMore(action)}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View More
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Bootstrap Standard Pagination */}
                    {totalPages > 1 && (
                      <div className="card-footer bg-light border-top">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                          <div className="text-muted small">
                            Showing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> 
                            <span className="ms-2">({totalActionsCount} total actions)</span>
                          </div>
                          <nav aria-label="Page navigation">
                            <ul className="pagination mb-0">
                              {/* First Page Button */}
                              <li className={`page-item ${currentPage === 1 || loading ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (!loading && currentPage > 1) {
                                      handlePageChange(1);
                                    }
                                  }}
                                  disabled={currentPage === 1 || loading}
                                  aria-label="First page"
                                >
                                  <span aria-hidden="true">&laquo;&laquo;</span>
                                  <span className="visually-hidden">First</span>
                                </button>
                              </li>
                              {/* Previous Page Button */}
                              <li className={`page-item ${currentPage === 1 || loading ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (!loading && currentPage > 1) {
                                      handlePageChange(currentPage - 1);
                                    }
                                  }}
                                  disabled={currentPage === 1 || loading}
                                  aria-label="Previous"
                                >
                                  <span aria-hidden="true">&laquo;</span>
                                  <span className="visually-hidden">Previous</span>
                                </button>
                              </li>
                              {(() => {
                                // Generate page numbers to show
                                const pages = [];
                                const maxVisible = 5;
                                
                                if (totalPages <= maxVisible) {
                                  // Show all pages
                                  for (let i = 1; i <= totalPages; i++) {
                                    pages.push(i);
                                  }
                                } else {
                                  // Show first page
                                  pages.push(1);
                                  
                                  if (currentPage <= 3) {
                                    // Near start: 1, 2, 3, 4, ..., last
                                    for (let i = 2; i <= 4; i++) {
                                      pages.push(i);
                                    }
                                    pages.push('ellipsis');
                                    pages.push(totalPages);
                                  } else if (currentPage >= totalPages - 2) {
                                    // Near end: 1, ..., last-3, last-2, last-1, last
                                    pages.push('ellipsis');
                                    for (let i = totalPages - 3; i <= totalPages; i++) {
                                      pages.push(i);
                                    }
                                  } else {
                                    // Middle: 1, ..., current-1, current, current+1, ..., last
                                    pages.push('ellipsis');
                                    pages.push(currentPage - 1);
                                    pages.push(currentPage);
                                    pages.push(currentPage + 1);
                                    pages.push('ellipsis');
                                    pages.push(totalPages);
                                  }
                                }
                                
                                return pages.map((page, idx) => {
                                  if (page === 'ellipsis') {
                                    return (
                                      <li key={`ellipsis-${idx}`} className="page-item disabled">
                                        <span className="page-link">...</span>
                                      </li>
                                    );
                                  }
                                  
                                  const isActive = currentPage === page;
                                  return (
                                    <li key={page} className={`page-item ${isActive ? 'active' : ''}`}>
                                      <button
                                        className="page-link"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (!loading) {
                                            handlePageChange(page);
                                          }
                                        }}
                                        disabled={loading}
                                      >
                                        {page}
                                      </button>
                                    </li>
                                  );
                                });
                              })()}
                              {/* Next Page Button */}
                              <li className={`page-item ${currentPage === totalPages || loading ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (!loading && currentPage < totalPages) {
                                      handlePageChange(currentPage + 1);
                                    }
                                  }}
                                  disabled={currentPage === totalPages || loading}
                                  aria-label="Next"
                                >
                                  <span aria-hidden="true">&raquo;</span>
                                  <span className="visually-hidden">Next</span>
                                </button>
                              </li>
                              {/* Last Page Button */}
                              <li className={`page-item ${currentPage === totalPages || loading ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (!loading && currentPage < totalPages) {
                                      handlePageChange(totalPages);
                                    }
                                  }}
                                  disabled={currentPage === totalPages || loading}
                                  aria-label="Last page"
                                >
                                  <span aria-hidden="true">&raquo;&raquo;</span>
                                  <span className="visually-hidden">Last</span>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'activities' && (
              <div className="card">
                {filteredActivities.length === 0 ? (
                  <div className="card-body text-center py-5">
                    <i className="fas fa-clipboard-list text-muted" style={{ fontSize: '3rem' }}></i>
                    <h5 className="mt-3 text-muted">
                      {activities.length === 0 ? 'No activities' : 'No activities match your filters'}
                    </h5>
                    <p className="text-muted">
                      {activities.length === 0 
                        ? "You haven't performed any activities yet."
                        : "Try adjusting your filters to see more results."
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="card-header bg-light">
                      <div className="row align-items-center">
                        <div className="col">
                          <small className="text-muted">
                            Showing {filteredActivities.length} of {activities.length} available activities
                          </small>
                        </div>
                        <div className="col text-end">
                          <small className="text-muted">
                            Total credit cost: {filteredActivities.reduce((sum, a) => sum + (a.credit_cost || 0), 0)}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="list-group list-group-flush">
                      {filteredActivities.map((activity, index) => (
                        <div key={index} className="list-group-item">
                          <div className="row align-items-center">
                            <div className="col-auto">
                              <div className="rounded-circle d-flex align-items-center justify-content-center" 
                                   style={customStyles.iconContainer}>
                                <img
                                  src={getActionIcon(activity.action_type)}
                                  alt={activity.action_type}
                                  width={20}
                                  height={20}
                                />
                              </div>
                            </div>
                            <div className="col">
                              <h6 className="mb-1 fw-semibold">
                                {activity.name || activity.action_type || 'Activity'}
                              </h6>
                              <small className="text-muted d-block">
                                Category: {activity.category || 'General'}
                              </small>
                              {activity.description && (
                                <small className="text-muted d-block mt-1">
                                  {activity.description}
                                </small>
                              )}
                            </div>
                            <div className="col-auto text-end">
                              <div className="fw-bold text-primary">
                                {activity.credit_cost || 0} credits
                              </div>
                              {/* <small className={`badge ${activity.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                {activity.is_active ? 'Active' : 'Inactive'}
                              </small> */}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Summary Card */}
            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <h4 className="card-title mb-4">Summary</h4>
                <div className="row g-4 mb-4">
                  <div className="col-md-3 text-center">
                    <div className="h2 fw-bold text-primary">
                      {balance?.credit_balance || 0}
                    </div>
                    <div className="text-muted small">Available Credits</div>
                  </div>
                  <div className="col-md-3 text-center">
                    <div className="h2 fw-bold text-success">
                      {totalActionsCount}
                    </div>
                    <div className="text-muted small">
                      Total Actions
                    </div>
                  </div>
                  <div className="col-md-3 text-center">
                    <div className="h2 fw-bold text-info">
                      {actions.reduce((sum, a) => sum + (a.credits_used || 0), 0)}
                    </div>
                    <div className="text-muted small">
                      Current Page Credits Used
                    </div>
                  </div>
                  <div className="col-md-3 text-center">
                    <div className="h2 fw-bold text-warning">
                      {uniqueCategories.length}
                    </div>
                    <div className="text-muted small">Available Categories</div>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="border-top pt-4">
                  <h5 className="mb-3">Quick Stats</h5>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Most Used Action:</span>
                        <span className="fw-medium">
                          {actions.length > 0 ? 
                            (() => {
                              const actionCounts = {};
                              actions.forEach(action => {
                                actionCounts[action.action_type] = (actionCounts[action.action_type] || 0) + 1;
                              });
                              const mostUsed = Object.entries(actionCounts)
                                .sort(([,a], [,b]) => b - a)[0];
                              return mostUsed ? mostUsed[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'None';
                            })() : 'None'
                          }
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Total Action Types:</span>
                        <span className="fw-medium">{uniqueActionTypes.length}</span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Average Credits per Action:</span>
                        <span className="fw-medium">
                          {actions.length > 0 ? 
                            (filteredActionsForDisplay.length > 0 
                              ? (filteredActionsForDisplay.reduce((sum, a) => sum + (a.credits_used || 0), 0) / filteredActionsForDisplay.length).toFixed(2) 
                              : '0.00') :
                            '0.00'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
      </div>

      {/* Action Detail Panel Component */}
      <ActionDetailPanel
        selectedAction={selectedAction}
        showDetailPanel={showDetailPanel}
        onClose={handleClosePanel}
        formatDate={formatDate}
      />
    </div>
  </div>
  );
};

export default BillingActivity;
